import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  EventEmitter,
  inject,
  Output,
  signal,
  DestroyRef,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HlmButton, HlmInput, HlmLabel, HlmSpinnerImports } from '@pagai-fe/ui';
import { PasswordInputComponent } from '../../../shared/components/password-input/password-input.component';
import { AuthenticateStateEnum, SignUpEnum } from '../../enums/authenticate.enum';
import { CognitoSignUpResult } from '../../interfaces/auth.interfaces';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-sign-up',
  standalone: true,
  imports: [
    CommonModule,
    HlmButton,
    HlmInput,
    FormsModule,
    ReactiveFormsModule,
    HlmLabel,
    HlmSpinnerImports,
    PasswordInputComponent,
  ],
  host: {
    class: 'block',
  },
  templateUrl: './sign-up.component.html',
})
export class SignUpComponent {
  private readonly _authService: AuthService = inject(AuthService);
  private readonly _destroyRef = inject(DestroyRef);

  protected readonly signUpEnum = SignUpEnum;
  protected readonly isLoading = signal(false);
  protected readonly signUpState = signal<SignUpEnum | undefined>(undefined);
  protected readonly hasError = computed(() => this.signUpState() === SignUpEnum.FAIL);

  @Output()
  protected readonly signUpEvent = new EventEmitter<AuthenticateStateEnum>();

  protected readonly form = new FormGroup({
    email: new FormControl<string | undefined>(undefined, {
      nonNullable: true,
      validators: [Validators.required, Validators.email],
    }),
    password: new FormControl<string | undefined>(undefined, {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  protected signUp(): void {
    this.isLoading.set(true);
    const { email, password } = this.form.value;
    if (email && password) {
      this._authService
        .signUp(email, password)
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          error: (_error: CognitoSignUpResult) => {
            this.signUpState.set(SignUpEnum.FAIL);
            this.isLoading.set(false);
          },
          complete: () => {
            this.signUpState.set(SignUpEnum.SUCCESS);
            this.isLoading.set(false);
          },
        });
    }
  }

  protected onSignInClick(): void {
    this.signUpEvent.emit(AuthenticateStateEnum.SIGN_IN);
  }
}
